import { flow } from 'fp-ts/function';
import { filter, find, forEach, keys, mergeWith, reduce, values } from 'lodash/fp';
import { Amount } from '@mews-commander/types';
import { isZeroAmount, safeSumAmounts, sumAllAmounts, sumAmounts } from '@mews-commander/utils';
import { AmountCurrencyMap } from '../types';

export const getTotalAmountAndCount = (items: { amount: Amount; unitCount?: number }[]) => ({
    amount: sumAllAmounts(items.map(({ amount }) => amount)),
    unitCount: items.reduce((acc, { unitCount = 0 }) => acc + unitCount, 0),
});

export const getAmountCurrencyMapfromAmount = (amount: Amount, accumulator: AmountCurrencyMap): AmountCurrencyMap => {
    if (!amount) return {};

    const acc = {
        ...accumulator,
        [amount.currency]: amount,
    };

    if (!amount.source) return acc;

    return getAmountCurrencyMapfromAmount(amount.source, acc);
};

export const sumAmountsIntoCurrencyMap = ([first, ...remaining]: Amount[]): AmountCurrencyMap => {
    const acc = getAmountCurrencyMapfromAmount(first, {});

    const summedAmounts = reduce(
        (result, value) => {
            const mappedAmount = getAmountCurrencyMapfromAmount(value, {});
            const resultingCurrencyMap: AmountCurrencyMap = {};

            flow(
                keys,
                filter((currency) => currency in result),
                forEach((currency: string) => {
                    resultingCurrencyMap[currency] = sumAmounts(result[currency], mappedAmount[currency]);
                })
            )(mappedAmount);

            return resultingCurrencyMap;
        },
        acc,
        remaining
    );

    return summedAmounts;
};

export const mergeCurrencyMaps = (first: AmountCurrencyMap, second: AmountCurrencyMap) =>
    mergeWith(safeSumAmounts, first, second);

export const getBalancedAmountFromCurrencyMap = (
    currencyMap: AmountCurrencyMap,
    defaultCurrencyCode: string,
    isMultiTaxEnvironment: boolean
) => {
    if (!currencyMap) return;

    const zeroValue = find(isZeroAmount, values(currencyMap));

    if (!isMultiTaxEnvironment && zeroValue) return zeroValue;

    return currencyMap[defaultCurrencyCode];
};
